import { QueryClientProvider } from '@tanstack/react-query';
import { Link, Outlet, createRootRoute } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { useApiError } from '@/hooks/use-api-error';
import { useLocale } from '@/hooks/use-locale';
import { captureError } from '@/lib/monitoring';
import { queryClient } from '@/lib/query-client';

function RootLayout() {
  const { locale, direction } = useLocale();

  // Keeps <html lang>/<html dir> in step with the active locale for the whole
  // session, including after a hot reload.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      storageKey="app_theme"
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster position={direction === 'rtl' ? 'top-left' : 'top-right'} dir={direction} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * The last stop for an error that escaped a route. It reports the failure and
 * shows the user a translated message with the reference id, rather than a
 * blank screen.
 */
function RouteErrorBoundary({ error }: { error: unknown }) {
  const { t } = useTranslation('common');
  const toMessage = useApiError();
  const failure = toMessage(error);

  useEffect(() => {
    captureError(error, { boundary: 'root' });
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-lg font-medium">{failure.message}</p>
      {failure.reference && <p className="text-sm text-muted-foreground">{failure.reference}</p>}
      <Button onClick={() => globalThis.location.reload()}>{t('actions.retry')}</Button>
    </div>
  );
}

/**
 * Rule E4 (case b): a router-level 404 is a different mechanism from an API
 * `NOT_FOUND` (no request was ever made), but the user is owed the same
 * things either way — an actual message and a way back, not a bare code.
 */
function RouteNotFound() {
  const { t } = useTranslation('exchange');
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-lg font-medium">{t('notFound')}</p>
      <Button asChild>
        <Link to="/">{t('backHome')}</Link>
      </Button>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFound,
});
